import { RefreshCw, Download, AlertCircle, Loader2, TrendingUp, TrendingDown, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { useState, useMemo } from 'react';

interface ReportViewProps {
    data: any;
    isLoading: boolean;
    isError: boolean;
    error: any;
    selectedMetrics: string[];
    selectedDimensions: string[];
    compareWithPrevious: boolean;
    includeGrowthAnalysis: boolean;
    onRefresh: () => void;
}

export function ReportView({
                               data,
                               isLoading,
                               isError,
                               error,
                               selectedMetrics,
                               selectedDimensions,
                               compareWithPrevious,
                               includeGrowthAnalysis,
                               onRefresh,
                           }: ReportViewProps) {
    // TODOS OS HOOKS NO TOPO - SEMPRE EXECUTADOS
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState<{
        key: string;
        direction: 'asc' | 'desc';
    } | null>(null);

    // Dados filtrados e ordenados - SEMPRE executado
    const filteredAndSortedData = useMemo(() => {
        // Se não houver dados, retorna array vazio
        if (!data || !data.data || data.data.length === 0) {
            return [];
        }

        let filtered = [...data.data];

        // Aplicar pesquisa
        if (searchTerm) {
            filtered = filtered.filter((row) => {
                return selectedDimensions.some((dim) => {
                    const value = row[dim];
                    return value && String(value).toLowerCase().includes(searchTerm.toLowerCase());
                });
            });
        }

        // Aplicar ordenação
        if (sortConfig) {
            filtered.sort((a, b) => {
                const aValue = a[sortConfig.key];
                const bValue = b[sortConfig.key];

                // Tratar valores nulos
                if (aValue === null || aValue === undefined) return 1;
                if (bValue === null || bValue === undefined) return -1;

                // Comparar números
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
                }

                // Comparar strings
                const aString = String(aValue).toLowerCase();
                const bString = String(bValue).toLowerCase();

                if (sortConfig.direction === 'asc') {
                    return aString.localeCompare(bString);
                } else {
                    return bString.localeCompare(aString);
                }
            });
        }

        return filtered;
    }, [data, searchTerm, sortConfig, selectedDimensions]);

    // FUNÇÕES (não são hooks, podem estar em qualquer lugar)
    const formatMetricName = (metric: string): string => {
        const names: { [key: string]: string } = {
            impressions: 'Impressões',
            clicks: 'Cliques',
            revenue: 'Receita',
            ecpm: 'eCPM',
            ctr: 'CTR',
            active_view: 'Viewability',
            unfilled_impressions: 'Não Preenchidas',
            requests_served: 'Requisições',
            pmr: 'PMR',
        };
        return names[metric] || metric;
    };

    const formatValue = (metric: string, value: any): string => {
        if (value === null || value === undefined) return '-';

        switch (metric) {
            case 'revenue':
                return `$ ${value.toFixed(2)}`;
            case 'ecpm':
                return `$ ${value.toFixed(2)}`;
            case 'ctr':
            case 'pmr':
            case 'active_view':
                return `${value.toFixed(2)}%`;
            case 'impressions':
            case 'clicks':
            case 'unfilled_impressions':
            case 'requests_served':
                return value.toLocaleString('pt-BR');
            default:
                return String(value);
        }
    };

    const getGrowthBadge = (growthPct: number) => {
        if (growthPct > 0) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 rounded">
                    <TrendingUp className="w-3 h-3" />
                    +{growthPct.toFixed(1)}%
                </span>
            );
        } else if (growthPct < 0) {
            return (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                    <TrendingDown className="w-3 h-3" />
                    {growthPct.toFixed(1)}%
                </span>
            );
        }
        return null;
    };

    const exportToCSV = () => {
        const headers = [...selectedDimensions, ...selectedMetrics];
        const csvContent = [
            headers.join(','),
            ...filteredAndSortedData.map((row: any) =>
                headers.map((key) => row[key] || '').join(',')
            ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `report_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleSort = (key: string) => {
        let direction: 'asc' | 'desc' = 'asc';

        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }

        setSortConfig({ key, direction });
    };

    const getSortIcon = (key: string) => {
        if (!sortConfig || sortConfig.key !== key) {
            return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
        }

        return sortConfig.direction === 'asc'
            ? <ArrowUp className="w-4 h-4 text-blue-600" />
            : <ArrowDown className="w-4 h-4 text-blue-600" />;
    };

    // AGORA SIM - CONDICIONAIS APÓS TODOS OS HOOKS
    // Loading
    if (isLoading) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                <div className="flex flex-col items-center justify-center gap-4">
                    <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
                    <p className="text-gray-600">Gerando relatório...</p>
                </div>
            </div>
        );
    }

    // Error
    if (isError) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8">
                <div className="flex items-start gap-4">
                    <AlertCircle className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-red-900 mb-2">
                            Erro ao gerar relatório
                        </h3>
                        <p className="text-red-700 mb-4">
                            {error?.message || 'Ocorreu um erro ao buscar os dados.'}
                        </p>
                        <button
                            onClick={onRefresh}
                            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            Tentar Novamente
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // No data
    if (!data || !data.data || data.data.length === 0) {
        return (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
                <div className="flex flex-col items-center justify-center gap-4 text-gray-500">
                    <AlertCircle className="w-12 h-12" />
                    <p className="text-lg font-medium">Nenhum dado encontrado</p>
                    <p className="text-sm">Tente ajustar os filtros ou período selecionado</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Comparison Summary */}
            {compareWithPrevious && data.comparison && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-900 mb-2">
                        📊 Comparação com Período Anterior
                    </h3>
                    <div className="text-sm text-blue-800">
                        <p>
                            Período anterior: {data.comparison.previous_period.start} a{' '}
                            {data.comparison.previous_period.end}
                        </p>
                    </div>
                </div>
            )}

            {/* Main Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                {/* Header com controles */}
                <div className="p-4 border-b border-gray-200">
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                        <div>
                            <h2 className="text-lg font-bold text-gray-900">Dados do Relatório</h2>
                            <p className="text-sm text-gray-600">
                                {filteredAndSortedData.length} de {data.data.length} registros
                            </p>
                        </div>

                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                            {/* Barra de pesquisa */}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Pesquisar..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full sm:w-64"
                                />
                            </div>

                            <button
                                onClick={onRefresh}
                                className="flex items-center justify-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Atualizar
                            </button>

                            <button
                                onClick={exportToCSV}
                                className="flex items-center justify-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                            >
                                <Download className="w-4 h-4" />
                                Exportar CSV
                            </button>
                        </div>
                    </div>
                </div>

                {/* Tabela */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                            {selectedDimensions.map((dim) => (
                                <th
                                    key={dim}
                                    onClick={() => handleSort(dim)}
                                    className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center gap-2">
                                            <span>
                                                {dim === 'ad_unit_name' ? 'Ad Unit' :
                                                    dim === 'domain' ? 'Domínio' :
                                                        dim === 'country' ? 'País' :
                                                            dim === 'date' ? 'Data' : dim}
                                            </span>
                                        {getSortIcon(dim)}
                                    </div>
                                </th>
                            ))}
                            {selectedMetrics.map((metric) => (
                                <th
                                    key={metric}
                                    onClick={() => handleSort(metric)}
                                    className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                                >
                                    <div className="flex items-center justify-end gap-2">
                                        <span>{formatMetricName(metric)}</span>
                                        {getSortIcon(metric)}
                                    </div>
                                </th>
                            ))}
                            {includeGrowthAnalysis && (
                                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                    Crescimento
                                </th>
                            )}
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                        {filteredAndSortedData.length === 0 ? (
                            <tr>
                                <td colSpan={selectedDimensions.length + selectedMetrics.length + (includeGrowthAnalysis ? 1 : 0)}
                                    className="px-4 py-8 text-center text-gray-500">
                                    Nenhum resultado encontrado para "{searchTerm}"
                                </td>
                            </tr>
                        ) : (
                            filteredAndSortedData.map((row: any, index: number) => (
                                <tr key={index} className="hover:bg-gray-50 transition-colors">
                                    {selectedDimensions.map((dim) => (
                                        <td key={dim} className="px-4 py-3 text-sm text-gray-900">
                                            {row[dim] || '-'}
                                        </td>
                                    ))}
                                    {selectedMetrics.map((metric) => (
                                        <td key={metric} className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                                            {formatValue(metric, row[metric])}
                                        </td>
                                    ))}
                                    {includeGrowthAnalysis && row.growth_analysis && (
                                        <td className="px-4 py-3 text-sm text-center">
                                            {row.growth_analysis.revenue_growth_pct !== undefined &&
                                                getGrowthBadge(row.growth_analysis.revenue_growth_pct)}
                                        </td>
                                    )}
                                </tr>
                            ))
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Metadata */}
            {data.metadata && (
                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <span className="text-gray-600">Período:</span>
                            <p className="font-medium text-gray-900">
                                {data.metadata.period.days} dias
                            </p>
                        </div>
                        <div>
                            <span className="text-gray-600">Registros:</span>
                            <p className="font-medium text-gray-900">{data.metadata.total_records}</p>
                        </div>
                        <div>
                            <span className="text-gray-600">Domínios:</span>
                            <p className="font-medium text-gray-900">
                                {data.metadata.filters_applied.domains}
                            </p>
                        </div>
                        {data.metadata.filters_applied.top_n && (
                            <div>
                                <span className="text-gray-600">Top N:</span>
                                <p className="font-medium text-gray-900">
                                    {data.metadata.filters_applied.top_n}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}